import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Grid, LinearProgress, Skeleton, Stack, Typography } from '@mui/material';
import { Refresh } from '@mui/icons-material';
import PageHeader from './PageHeader';

interface Props {
  page: 'dashboard' | 'analytics';
  error?: unknown;
  onRetry: () => void;
}

/** Initial loading and failure states keep the page structure visible. */
const FleetPageState: React.FC<Props> = ({ page, error, onRetry }) => {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(() => setSlow(true), 10000);
    return () => window.clearTimeout(timer);
  }, []);
  const title = page === 'dashboard' ? 'Fleet Dashboard' : 'Fleet Analytics & Reports';
  const description = page === 'dashboard'
    ? 'Monitor vehicle activity, health, and fleet performance'
    : 'Explore mileage, fuel usage, engine health, and fleet performance';
  const requestError = error as { code?: string; response?: { status?: number } } | undefined;
  const message = requestError?.code === 'ECONNABORTED'
    ? 'The request took too long. Please try again.'
    : requestError?.response?.status === 403
      ? 'You do not have access to this data. Check your workspace access.'
      : 'We couldn’t load your fleet data. Check your connection and try again.';

  return (
    <Box>
      <PageHeader title={title} description={description} icon={page} />
      {error ? (
        <Alert severity="error" action={<Button color="inherit" startIcon={<Refresh />} onClick={onRetry}>Retry</Button>} sx={{ mb: 3 }}>
          <Typography variant="subtitle2">Unable to load {page === 'dashboard' ? 'dashboard' : 'analytics'}</Typography>
          {message}
        </Alert>
      ) : (
        <Card variant="outlined" sx={{ mb: 3, bgcolor: 'background.paper', color: 'text.primary', borderRadius: 3 }}>
          <CardContent>
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2} sx={{ mb: 1.5 }}>
              <Typography role="status" variant="subtitle2">
                {slow ? 'This is taking longer than usual…' : 'Loading your fleet data…'}
              </Typography>
              <Typography variant="caption" color="text.secondary">{page === 'dashboard' ? 'Last 7 days' : 'Preparing reports'}</Typography>
            </Stack>
            <LinearProgress aria-label={`Loading ${page}`} sx={{ borderRadius: 2, height: 5 }} />
            {slow && <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>We’re still waiting for a response. If the request fails, you can retry here.</Typography>}
          </CardContent>
        </Card>
      )}
      {!error && <Box aria-hidden="true">
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[0, 1, 2, 3].map(item => <Grid item xs={6} md={3} key={item}>
            <Card variant="outlined" sx={{ borderRadius: 3, bgcolor: 'background.paper' }}>
              <CardContent><Skeleton width="65%" /><Skeleton width="35%" height={58} /><Skeleton width="80%" /></CardContent>
            </Card>
          </Grid>)}
        </Grid>
        <Grid container spacing={3}>
          {[0, 1, 2, 3].map(item => <Grid item xs={12} md={6} key={item}>
            <Card variant="outlined" sx={{ borderRadius: 3, bgcolor: 'background.paper' }}>
              <CardContent><Skeleton width="45%" height={32} /><Skeleton variant="rounded" height={200} sx={{ mt: 2 }} /></CardContent>
            </Card>
          </Grid>)}
        </Grid>
      </Box>}
    </Box>
  );
};

export default FleetPageState;
